import { useCallback, useEffect, useState } from 'react';
import { ActivityDefinition, Coding } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getCodeableConceptCode } from '@bariendo/lib/services';
import { APPOINTMENT_TYPES_SYSTEM } from '@bariendo/lib/constants';
import { expandCodeSystem } from '@bariendo/lib/codesystem';

export interface UseAppointmentTypesResult {
  activityCodeSystem: Coding[] | null;
  getActivityDefinitionForAppointmentType: (code: string | undefined) => ActivityDefinition | undefined;
}

/**
 * Minimal hook to load appointment-related ActivityDefinitions and the appointment-types CodeSystem.
 */
export function useAppointmentTypesForScheduling(): UseAppointmentTypesResult {
  const medplum = useMedplum();

  const [activityDefinitions, setActivityDefinitions] = useState<Map<string, ActivityDefinition>>(new Map());
  const [activityCodeSystem, setActivityCodeSystem] = useState<Coding[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAppointmentTypes = async () => {
      try {
        const [definitionsBundle, codeSystem] = await Promise.all([
          medplum.searchResources('ActivityDefinition', {
            status: 'active',
            _count: '100',
          }),
          expandCodeSystem(medplum, APPOINTMENT_TYPES_SYSTEM),
        ]);

        if (cancelled) {
          return;
        }

        const definitionsMap = new Map<string, ActivityDefinition>();
        for (const def of (definitionsBundle ?? []) as ActivityDefinition[]) {
          const code = getCodeableConceptCode(def.code);
          if (code) {
            definitionsMap.set(code, def);
          }
        }

        setActivityDefinitions(definitionsMap);
        setActivityCodeSystem(codeSystem);
      } catch {
        // Swallow errors here; consumers can handle null / undefined data.
        setActivityDefinitions(new Map());
        setActivityCodeSystem(null);
      }
    };

    loadAppointmentTypes();

    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const getActivityDefinitionForAppointmentType = useCallback(
    (code: string | undefined) => {
      if (!code) return undefined;
      return activityDefinitions.get(code);
    },
    [activityDefinitions]
  );

  return {
    activityCodeSystem,
    getActivityDefinitionForAppointmentType,
  };
}
